/**
 * @license
 * Copyright 2025 Steven Roussey <sroussey@gmail.com>
 * SPDX-License-Identifier: Apache-2.0
 */

import type { IExecuteContext, TaskConfig } from "@workglow/task-graph";
import { CreateWorkflow, Task, TaskInvalidInputError, Workflow } from "@workglow/task-graph";
import type {
  DataPortSchema,
  FromSchema,
  TypedArray,
  TypedArraySchemaOptions,
} from "@workglow/util/schema";
import { createTypedArrayFrom, TypedArraySchema } from "@workglow/util/schema";

const inputSchema = {
  type: "object",
  properties: {
    vectors: {
      type: "array",
      items: TypedArraySchema({
        title: "Vector",
        description: "Vector for component-wise product",
      }),
      title: "Vectors",
      description: "Array of vectors to multiply component-wise",
    },
  },
  required: ["vectors"],
  additionalProperties: false,
} as const satisfies DataPortSchema;

const outputSchema = {
  type: "object",
  properties: {
    result: TypedArraySchema({
      title: "Result",
      description: "Component-wise product (Hadamard product)",
    }),
  },
  required: ["result"],
  additionalProperties: false,
} as const satisfies DataPortSchema;

export type VectorMultiplyTaskInput = FromSchema<typeof inputSchema, TypedArraySchemaOptions>;
export type VectorMultiplyTaskOutput = FromSchema<typeof outputSchema, TypedArraySchemaOptions>;

export class VectorMultiplyTask<
  Input extends VectorMultiplyTaskInput = VectorMultiplyTaskInput,
  Output extends VectorMultiplyTaskOutput = VectorMultiplyTaskOutput,
  Config extends TaskConfig = TaskConfig,
> extends Task<Input, Output, Config> {
  static override readonly type = "VectorMultiplyTask";
  static override readonly category = "Vector";
  public static override title = "Multiply";
  public static override description =
    "Returns the component-wise product (Hadamard product) of all vectors";

  static override inputSchema() {
    return inputSchema;
  }

  static override outputSchema() {
    return outputSchema;
  }

  override async execute(input: Input, _context: IExecuteContext): Promise<Output> {
    const { vectors } = input as { vectors: TypedArray[] };
    if (vectors.length === 0) {
      throw new TaskInvalidInputError("At least one vector is required");
    }
    const len = vectors[0].length;
    for (let i = 1; i < vectors.length; i++) {
      if (vectors[i].length !== len) {
        throw new TaskInvalidInputError("All vectors must have the same length");
      }
    }
    const values = Array.from({ length: len }, (_, i) =>
      vectors.reduce((acc, v) => acc * Number(v[i]), 1)
    );
    return { result: createTypedArrayFrom(vectors, values) } as Output;
  }
}

declare module "@workglow/task-graph" {
  interface Workflow {
    vectorMultiply: CreateWorkflow<VectorMultiplyTaskInput, VectorMultiplyTaskOutput, TaskConfig>;
  }
}

Workflow.prototype.vectorMultiply = CreateWorkflow(VectorMultiplyTask);
